import { useState, useRef, useEffect, useLayoutEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { SlidersHorizontal, X, Check } from 'lucide-react';
import Dropdown from '../receptura/Dropdown';
import { MODAL_SKLO } from '../../lib/uiTokens';
import { SkloCtx, skloMaterial } from '../ui/Sklo';

/**
 * Filtr seznamu — tlačítko vedle hledání + panel s volbami (norma FofrLama OS,
 * patří do `ListToolbar` slotu `filtr`).
 *
 * Pohled (pill pod hledáním) = předem definovaná sada dat, vždy právě jeden.
 * Filtr = vlastní zúžení té sady; skupiny se kombinují (A ZÁROVEŇ), volby
 * uvnitř skupiny se sčítají (NEBO).
 *
 * @param {Array} props.skupiny
 *   `{ id, label, labelKratky?, typ: 'volby', volby: [{ value, label, pocet? }] }` — přepínací čipy
 *   (`labelKratky` se použije v čipu aktivního filtru, kde by dlouhý nadpis skupiny překážel)
 *   `{ id, label, typ: 'dropdown', options, placeholder, vychozi? }` — naše `Dropdown` (jedna hodnota).
 *   Kterákoli skupina smí mít `doplnek` (JSX) — vykreslí se pod volbou, třeba
 *   pole „od–do" k volbě „Vlastní období".
 *   `vychozi` = hodnota, která znamená „nefiltruje se" (typicky vypsaná volba „Vše"); s ní se
 *   skupina nepočítá do odznaku na tlačítku ani nedává čip nad seznam.
 * @param {Object} props.hodnoty - `{ [id]: string[] }` u voleb, `{ [id]: value|null }` u dropdownu
 * @param {(id: string, hodnota: any) => void} props.onChange
 * @param {() => void} props.onReset
 * @param {number} [props.pocetVysledku] - kolik položek filtr propouští (tlačítko v patičce)
 */
export function FiltrTlacitko({ skupiny, hodnoty, onChange, onReset, pocetVysledku, siroky = true, kompaktni = false, isDark, mutedColor, barva = '#F59E0B' }) {
    const [otevreno, setOtevreno] = useState(false);
    const [pozice, setPozice] = useState(null);
    const tlacitkoRef = useRef(null);
    // V hlavičce ve skle (mobil) skleněný kruh 44 px jako ostatní ovládání
    const sklo = useContext(SkloCtx) && kompaktni;
    const panelRef = useRef(null);

    const pocetAktivnich = pocetAktivnichFiltru(skupiny, hodnoty);

    const spocitejPozici = () => {
        const r = tlacitkoRef.current?.getBoundingClientRect();
        if (!r) return;
        const sirka = 380;
        const left = Math.min(Math.max(8, r.left), window.innerWidth - sirka - 8);
        setPozice({ left, top: r.bottom + 6, sirka, maxVyska: window.innerHeight - r.bottom - 20 });
    };

    useLayoutEffect(() => { if (otevreno) spocitejPozici(); }, [otevreno]);

    useEffect(() => {
        if (!otevreno) return;
        const mimo = (e) => {
            if (tlacitkoRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
            // Otevřený Dropdown uvnitř filtru má panel v portálu — klik do něj není „mimo".
            if (e.target.closest?.('[data-portal-panel]')) return;
            setOtevreno(false);
        };
        const klavesa = (e) => { if (e.key === 'Escape') setOtevreno(false); };
        document.addEventListener('mousedown', mimo);
        document.addEventListener('keydown', klavesa);
        window.addEventListener('resize', spocitejPozici);
        window.addEventListener('scroll', spocitejPozici, true);
        return () => {
            document.removeEventListener('mousedown', mimo);
            document.removeEventListener('keydown', klavesa);
            window.removeEventListener('resize', spocitejPozici);
            window.removeEventListener('scroll', spocitejPozici, true);
        };
    }, [otevreno]);

    const hranice = isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)';
    const aktivniTlacitko = otevreno || pocetAktivnich > 0;

    const prepniVolbu = (skupina, value) => {
        const puvodni = hodnoty[skupina.id] || [];
        onChange(skupina.id, puvodni.includes(value) ? puvodni.filter(v => v !== value) : [...puvodni, value]);
    };

    return (
        <>
            <button ref={tlacitkoRef} type="button" onClick={() => setOtevreno(o => !o)}
                title="Filtrovat položky" aria-label="Filtr" aria-expanded={otevreno}
                className="flex items-center justify-center rounded-lg"
                style={sklo ? {
                    width: 44, height: 44, flexShrink: 0, position: 'relative', cursor: 'pointer',
                    borderRadius: 999, border: 'none',
                    ...skloMaterial(isDark),
                    ...(aktivniTlacitko ? { background: `${barva}33` } : null),
                    color: aktivniTlacitko ? barva : 'var(--text-primary)',
                    WebkitTapHighlightColor: 'transparent',
                } : {
                    // Na dotyku 40×40 jako ostatní tlačítka hlavičky — filtr je
                    // na mobilu nástroj kontroly, ne okrajová funkce.
                    height: kompaktni ? 40 : 32, flexShrink: 0, gap: 6, cursor: 'pointer',
                    padding: siroky ? '0 11px' : 0,
                    width: siroky ? 'auto' : (kompaktni ? 40 : 32), position: 'relative',
                    fontSize: 12, fontWeight: 600,
                    border: `1px solid ${aktivniTlacitko ? `${barva}66` : hranice}`,
                    background: aktivniTlacitko ? `${barva}1a` : 'transparent',
                    color: aktivniTlacitko ? barva : 'var(--text-secondary, #94a3b8)',
                }}>
                <SlidersHorizontal size={sklo ? 19 : 15} />
                {siroky && <span>Filtr</span>}
                {pocetAktivnich > 0 && (
                    <span style={{
                        minWidth: 16, height: 16, borderRadius: 999, padding: '0 4px',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 700, background: barva, color: '#fff',
                        ...(siroky ? null : { position: 'absolute', top: -5, right: -5 }),
                    }}>{pocetAktivnich}</span>
                )}
            </button>

            {otevreno && pozice && createPortal(
                <div ref={panelRef} role="dialog" aria-label="Filtr" style={{
                    position: 'fixed', zIndex: 9999, left: pozice.left, top: pozice.top, width: pozice.sirka,
                    maxHeight: Math.max(240, pozice.maxVyska), display: 'flex', flexDirection: 'column',
                    ...MODAL_SKLO, overflow: 'hidden',
                }}>
                    <div style={{ overflowY: 'auto', padding: '14px 14px 6px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {skupiny.map(skupina => (
                            <div key={skupina.id}>
                                <div className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: mutedColor, marginBottom: 6 }}>
                                    {skupina.label}
                                </div>
                                {skupina.typ === 'dropdown' ? (
                                    <Dropdown value={hodnoty[skupina.id] ?? ''} onChange={(v) => onChange(skupina.id, v === '' ? null : v)}
                                        options={skupina.options} placeholder={skupina.placeholder} isDark={isDark} height={32} accent={barva} />
                                ) : (
                                    <div className="flex flex-wrap" style={{ gap: 6 }}>
                                        {skupina.volby.map(v => {
                                            const vybrano = (hodnoty[skupina.id] || []).includes(v.value);
                                            const prazdna = v.pocet === 0 && !vybrano;
                                            return (
                                                <button key={v.value} type="button" onClick={() => prepniVolbu(skupina, v.value)}
                                                    className="inline-flex items-center rounded-full text-[11.5px] font-medium"
                                                    style={{
                                                        gap: 5, padding: '4px 10px', cursor: 'pointer',
                                                        border: `1px solid ${vybrano ? `${barva}88` : hranice}`,
                                                        background: vybrano ? `${barva}1f` : 'transparent',
                                                        color: vybrano ? barva : 'var(--text-primary)',
                                                        opacity: prazdna ? 0.45 : 1,
                                                    }}>
                                                    {vybrano && <Check size={11} strokeWidth={3} />}
                                                    {v.label}
                                                    {v.pocet != null && (
                                                        <span className="tabular-nums" style={{ fontSize: 10.5, color: vybrano ? barva : mutedColor }}>{v.pocet}</span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                                {skupina.doplnek && <div style={{ marginTop: 8 }}>{skupina.doplnek}</div>}
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center justify-between" style={{ gap: 8, padding: '10px 14px', borderTop: `1px solid ${hranice}` }}>
                        <button type="button" onClick={onReset} disabled={pocetAktivnich === 0}
                            className="text-[12px] font-medium"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: pocetAktivnich ? 'pointer' : 'default', color: mutedColor, opacity: pocetAktivnich ? 1 : 0.4 }}>
                            Zrušit filtry
                        </button>
                        <button type="button" onClick={() => setOtevreno(false)}
                            className="text-[12px] font-semibold rounded-lg"
                            style={{ background: barva, color: '#fff', border: 'none', padding: '6px 12px', cursor: 'pointer' }}>
                            {pocetVysledku != null ? `Zobrazit ${pocetVysledku}` : 'Hotovo'}
                        </button>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
}

/**
 * Řádek aktivních filtrů pod pohledy — co je zapnuté, je vidět i se zavřeným
 * panelem a jde to jedním klikem zrušit.
 */
export function AktivniFiltry({ skupiny, hodnoty, onChange, onReset, isDark, mutedColor, barva = '#F59E0B' }) {
    const cipy = [];
    for (const skupina of skupiny) {
        if (skupina.typ === 'dropdown') {
            const v = hodnoty[skupina.id];
            if (jeNefiltrovano(skupina, v)) continue;
            const moznost = skupina.options.find(o => String(o.value) === String(v));
            cipy.push({ klic: `${skupina.id}:${v}`, text: `${skupina.labelKratky || skupina.label}: ${moznost?.label ?? v}`, zrus: () => onChange(skupina.id, null) });
        } else {
            for (const v of hodnoty[skupina.id] || []) {
                const volba = skupina.volby.find(o => o.value === v);
                cipy.push({
                    klic: `${skupina.id}:${v}`,
                    text: `${skupina.labelKratky || skupina.label}: ${volba?.label ?? v}`,
                    zrus: () => onChange(skupina.id, (hodnoty[skupina.id] || []).filter(x => x !== v)),
                });
            }
        }
    }
    if (!cipy.length) return null;

    return (
        <div className="flex items-center flex-wrap" style={{ gap: 6 }}>
            {cipy.map(c => (
                <span key={c.klic} className="inline-flex items-center rounded-full text-[11px] font-medium"
                    style={{ gap: 4, padding: '2px 4px 2px 9px', background: `${barva}1a`, color: barva, border: `1px solid ${barva}44` }}>
                    {c.text}
                    <button type="button" onClick={c.zrus} title="Odebrat filtr" aria-label={`Odebrat ${c.text}`}
                        className="flex items-center justify-center rounded-full"
                        style={{ width: 16, height: 16, background: 'none', border: 'none', cursor: 'pointer', color: barva, padding: 0 }}>
                        <X size={11} strokeWidth={2.5} />
                    </button>
                </span>
            ))}
            <button type="button" onClick={onReset} className="text-[11px] font-medium"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor, padding: '2px 4px' }}
                onMouseEnter={e => { e.currentTarget.style.color = isDark ? '#fff' : 'var(--text-primary)'; }}
                onMouseLeave={e => { e.currentTarget.style.color = mutedColor; }}>
                Zrušit vše
            </button>
        </div>
    );
}

export function pocetAktivnichFiltru(skupiny, hodnoty) {
    return skupiny.reduce((s, sk) => {
        const h = hodnoty[sk.id];
        if (sk.typ === 'dropdown') return s + (jeNefiltrovano(sk, h) ? 0 : 1);
        return s + (h?.length || 0);
    }, 0);
}

/**
 * „Tahle hodnota znamená, že se nefiltruje."
 *
 * U většiny dropdownů je to prázdno. Některé ale mají vypsanou volbu „Vše"
 * s vlastní hodnotou, protože ji člověk musí umět v nabídce vybrat zpátky —
 * a ta se pak tvářila jako zapnutý filtr: nad seznamem svítil čip
 * „Období: Vše ×" a na tlačítku „Filtr 1", i když byl výchozí stav. Skupina
 * to řekne přes `vychozi`.
 */
function jeNefiltrovano(skupina, hodnota) {
    if (hodnota == null || hodnota === '') return true;
    return skupina.vychozi != null && String(hodnota) === String(skupina.vychozi);
}
